/**
 * One stage of the Overview's pipeline.
 * @file src/web/features/admin/components/StageRow.tsx
 *
 * A mark (done, current, needs you, waiting), the stage's name and what is
 * known about it, its due line, when the portal was last asked, and a warning
 * when oddits and the portal disagree.
 */
import { AlertTriangle, Check, Clock } from 'lucide-react';
import type { Freshness } from '../lib/portal-status';

export type StageState = 'done' | 'current' | 'blocked' | 'pending';

export function StageRow({ n, title, detail, state, due, checked, warning, action, last }: {
  n: number; title: string; detail: string; state: StageState; due?: string | null;
  checked?: Freshness | null; warning?: string | null;
  action?: { label: string; on: () => void }; last?: boolean;
}) {
  const mark =
    state === 'done' ? { ring: 'bg-ok-600 text-white', icon: <Check className="h-3.5 w-3.5" /> }
      : state === 'blocked' ? { ring: 'bg-warn-500 text-white', icon: <AlertTriangle className="h-3.5 w-3.5" /> }
        : state === 'current' ? { ring: 'bg-inkblack text-highlighter', icon: <span className="text-[11px] font-bold">{n}</span> }
          : { ring: 'bg-ink-100 text-ink-400', icon: <span className="text-[11px] font-bold">{n}</span> };
  return (
    <div className="relative flex gap-3 pb-5 last:pb-0" data-state={state}>
      {/* the thread between stages, so the sequence reads as one run */}
      {!last && <span className={`absolute left-3 top-7 h-[calc(100%-1.25rem)] w-px ${state === 'done' ? 'bg-ok-300' : 'bg-ink-200'}`} />}
      <span className={`relative z-10 grid size-6 shrink-0 place-items-center rounded-full ${mark.ring}`}>{mark.icon}</span>
      <div className={`min-w-0 flex-1 ${state === 'pending' ? 'opacity-60' : ''}`}>
        <div className="flex flex-wrap items-center gap-2">
          <p className={`text-sm ${state === 'current' || state === 'blocked' ? 'font-semibold text-ink-900' : 'font-medium text-ink-800'}`}>{title}</p>
          {state === 'current' && <span className="rounded-full bg-inkblack px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-highlighter">You are here</span>}
          {state === 'blocked' && <span className="rounded-full bg-warn-100 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-warn-700">Needs you</span>}
        </div>
        <p className="mt-0.5 text-xs text-ink-600">{detail}</p>
        {due && <p className="mt-1 flex items-center gap-1 text-[11px] text-ink-500"><Clock className="h-3 w-3" />{due}</p>}
        {checked && <p className={`mt-0.5 text-[11px] ${checked.state === 'fresh' ? 'text-ink-500' : 'text-warn-700'}`} data-freshness={checked.state}>{checked.text}</p>}
        {warning && <p role="alert" className="mt-1.5 flex items-start gap-1.5 rounded-lg border border-warn-200 bg-warn-50 px-2.5 py-1.5 text-[11px] font-medium text-warn-800"><AlertTriangle className="mt-px h-3 w-3 shrink-0" />{warning}</p>}
        {action && (state === 'current' || state === 'blocked') && (
          <button onClick={action.on} className="mt-2 rounded-full border border-ink-300 bg-paper px-3 py-1.5 text-xs font-semibold text-ink-700 hover:bg-ink-50">{action.label}</button>
        )}
      </div>
    </div>
  );
}
